/**
 * StatusTextSlider — animated text ticker that slides new text up from below
 * while the old text slides up and out, in a clipped vertical container.
 *
 * Ported from copilot-tauri-app's ActivityTicker.
 *
 * Enforces a minimum display time so rapid-fire updates don't feel jarring.
 * If new text arrives before the minimum time elapses, only the latest value
 * is queued and shown once the window opens.
 */

import {useState, useEffect, useRef, useCallback} from 'react'
import {clsx} from 'clsx'
import styles from './StatusTextSlider.module.css'

const TICKER_ANIMATION_MS = 150
const TICKER_MIN_DISPLAY_MS = 1000

interface TickerItem {
  text: string
  key: number
}

interface StatusTextSliderProps {
  text: string
}

export function StatusTextSlider({text}: StatusTextSliderProps) {
  const [items, setItems] = useState<TickerItem[]>(() => [{text, key: 0}])
  const nextKeyRef = useRef(1)
  const prevTextRef = useRef(text)
  const lastTransitionRef = useRef(0)
  const pendingRef = useRef<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pruneRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const transition = useCallback((newText: string) => {
    prevTextRef.current = newText
    lastTransitionRef.current = Date.now()
    const k = nextKeyRef.current++
    setItems(prev => {
      const last = prev[prev.length - 1] ?? {text: '', key: 0}
      return [last, {text: newText, key: k}]
    })

    if (pruneRef.current) clearTimeout(pruneRef.current)
    pruneRef.current = setTimeout(() => {
      pruneRef.current = null
      setItems(prev => {
        if (prev.length <= 1) return prev
        const last = prev[prev.length - 1]
        return last ? [last] : prev
      })
    }, TICKER_ANIMATION_MS)
  }, [])

  useEffect(() => {
    if (text === prevTextRef.current) {
      pendingRef.current = null
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    const elapsed = Date.now() - lastTransitionRef.current
    if (elapsed >= TICKER_MIN_DISPLAY_MS) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      pendingRef.current = null
      transition(text)
    } else {
      pendingRef.current = text
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null
          if (pendingRef.current && pendingRef.current !== prevTextRef.current) {
            const queued = pendingRef.current
            pendingRef.current = null
            transition(queued)
          }
        }, TICKER_MIN_DISPLAY_MS - elapsed)
      }
    }
  }, [text, transition])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (pruneRef.current) clearTimeout(pruneRef.current)
    }
  }, [])

  const isTransitioning = items.length > 1

  return (
    <span className={styles.slider} aria-live="polite">
      {items.map((item, index) => {
        const isExiting = isTransitioning && index === 0
        const isEntering = isTransitioning && index === items.length - 1
        return (
          <span
            key={item.key}
            className={clsx(styles.item, isExiting && styles.exiting, isEntering && styles.entering)}
          >
            {item.text}
          </span>
        )
      })}
    </span>
  )
}
